/* eslint-disable no-prototype-builtins */
import { Suspense } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { withLDProvider, useFlags } from 'launchdarkly-react-client-sdk';
import 'react-toastify/dist/ReactToastify.css';
// import { ReactQueryDevtools } from 'react-query/devtools';
import { ToastContainer } from 'react-toastify';
import { ErrorBoundary } from '@sentry/react';
import { AwsRum } from 'aws-rum-web';

import SignUp from './screens/auth/signup';

// authenticated routes
import Dashboard from './screens/authenticated/dashboard/dashboard';
import Login from './screens/auth/login';
import LdSetup from './screens/auth/ldSetup';
import ResetPassword from './screens/auth/resetPassword';

import AuthLayout from './layouts/authenticated';
import BasicLayout from './layouts/basic';

import Royalties from './screens/authenticated/dashboard/royalties/royalties';
import Songs from './screens/authenticated/dashboard/songs/songs';
import SongsCreateUpdate from './screens/authenticated/dashboard/songs/songsCreateUpdate';
import Gigs from './screens/authenticated/dashboard/gigs/gigs';
import GigsClaim from './screens/authenticated/dashboard/gigs/gigsClaim';
import Setlists from './screens/authenticated/dashboard/gigs/setlists';

import Opportunities from './screens/authenticated/dashboard/opportunities/opportunities';
import SignUpMessage from './screens/auth/signup-message';
import SignUpConfirm from './screens/auth/signup-confirm';
import AddAccount1 from './screens/onboarding/addAccount1';
import AddAccount2 from './screens/onboarding/addAccount2';
import AddAccount3 from './screens/onboarding/addAccount3';
import AddAccount4 from './screens/onboarding/addAccount4';
import AddAccount5 from './screens/onboarding/addAccount5';
import AddAccount6 from './screens/onboarding/addAccount6';
import AddAccountFinal from './screens/onboarding/addAccountFinal';
import Writers from './screens/authenticated/dashboard/writers/writers';
import WritersCreateUpdate from './screens/authenticated/dashboard/writers/writersCreateUpdate';
import Onboarding1 from './screens/onboarding/onboarding1';
import Onboarding2 from './screens/onboarding/onboarding2';
import Onboarding3 from './screens/onboarding/onboarding3';
import AppState from './store/appstate';
import ResetPasswordMessage from './screens/auth/resetPasswordMessage';
import ResetPasswordConfirm from './screens/auth/resetPasswordConfirm';
import ResetPasswordSuccess from './screens/auth/resetPasswordSuccess';
import AddTwitter from './screens/onboarding/addTwitter';
import AddInstagram from './screens/onboarding/addInstagram';
import AddFacebook from './screens/onboarding/addFacebook';
import AddSongkick from './screens/onboarding/addSongkick';
import AddBandsintown from './screens/onboarding/addBandsintown';
import OpportunitiesApply from './screens/authenticated/dashboard/opportunities/opportunitiesApply';
import Syncs from './screens/authenticated/dashboard/syncs/syncs';
import SyncsApply from './screens/authenticated/dashboard/syncs/syncsApply';
import Broadcasts from './screens/authenticated/dashboard/broadcasts/broadcasts';
import BroadcastsCreateUpdate from './screens/authenticated/dashboard/broadcasts/broadcastsCreateUpdate';
import SetlistsCreateUpdate from './screens/authenticated/dashboard/gigs/setlistsCreateUpdate';
import Tracking from './helpers/tracking';
import Terms from './screens/pages/terms';
import AddAccountPre from './screens/onboarding/addAccountPre';
import Settings from './screens/authenticated/dashboard/settings/settings';
import Statements from './screens/authenticated/dashboard/statements/statements';
import MaintenanceMode from './screens/pages/maintenanceMode';
// import maintenanceMode from "./screens/pages/maintenanceMode";

// if (process.env.REACT_APP_STAGE === 'prd') {
  try {
    const config = {
      sessionSampleRate: 1,
      guestRoleArn: 'arn:aws:iam::589126456693:role/RUM-Monitor-eu-west-1-589126456693-3425148983861-Unauth',
      identityPoolId: 'eu-west-1:3517ae3d-64c2-4187-9440-a0a1844ae0c4',
      endpoint: 'https://dataplane.rum.eu-west-1.amazonaws.com',
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: false,
    };
    const APPLICATION_ID = '2f805d61-28e9-4fac-a724-b63ea62e633e';
    const APPLICATION_VERSION = '1.0.0';
    const APPLICATION_REGION = 'eu-west-1';
    // eslint-disable-next-line no-unused-vars
    const rum = new AwsRum(
      APPLICATION_ID,
      APPLICATION_VERSION,
      APPLICATION_REGION,
      config,
    );
  } catch (error) {
    console.log(error);
    // Ignore errors thrown during CloudWatch RUM web client initialization
  }
// }

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
    },
  },
});

const PrivateRoute = ({ children, ...rest }) => {
  const accessToken = AppState.useState((s) => s?.authentication?.access_token);

  return (
    <Route
      {...rest}
      render={({ location: routeLocation }) => {
        const { pathname } = routeLocation;
        if (accessToken && accessToken.length > 0) {
          return children;
        }
          return (
            <Redirect
              to={{
                pathname: '/login',
                state: { redirectUrl: pathname },
              }}
            />
          );
      }}
    />
  );
};

function App() {
  const authentication = AppState.useState((s) => s?.authentication);
  const accessToken = authentication?.access_token;
  const flags = useFlags();
  const maintenanceEnabled = flags['maintenance-mode'];
  return (
    <div>
      {maintenanceEnabled ? (
        <MaintenanceMode />
      ) : (
        <ErrorBoundary
          fallback={({ error, componentStack }) => (
            <>
              <div>You have encountered an error</div>
              <div>{error.toString()}</div>
              <div>{componentStack}</div>
            </>
          )}
        >
          <QueryClientProvider client={queryClient}>
            {/* <ReactQueryDevtools initialIsOpen={false} /> */}
            <LdSetup />
            <ToastContainer />
            {/* suspense gives optional fallback if translation tool fails eg spinner/message */}
            <Suspense fallback={null}>
              <Router>
                {!!accessToken && <Tracking />}
                <Switch>
                  <PrivateRoute exact path="/">
                    <AuthLayout>
                      <Dashboard />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/royalties">
                    <AuthLayout>
                      <Royalties />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/gigs">
                    <AuthLayout>
                      <Gigs />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/gigs/claim">
                    <AuthLayout>
                      <GigsClaim key="create" />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/gigs/claim/update/:id">
                    <AuthLayout>
                      <GigsClaim key="update" />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/gigs/setlists">
                    <AuthLayout>
                      <Setlists />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/gigs/setlists/create">
                    <AuthLayout>
                      <SetlistsCreateUpdate key="create" />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/gigs/setlists/update/:id">
                    <AuthLayout>
                      <SetlistsCreateUpdate key="update" />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/writers">
                    <AuthLayout>
                      <Writers />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/writers/create">
                    <AuthLayout>
                      <WritersCreateUpdate key="create" />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/writers/update/:id">
                    <AuthLayout>
                      <WritersCreateUpdate key="update" />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/songs">
                    <AuthLayout>
                      <Songs />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/songs/create">
                    <AuthLayout>
                      <SongsCreateUpdate key="create" />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/songs/update/:id">
                    <AuthLayout>
                      <SongsCreateUpdate key="update" />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/opportunities">
                    <AuthLayout>
                      <Opportunities />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/opportunities/apply">
                    <AuthLayout>
                      <OpportunitiesApply key="create" />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/opportunities/apply/:id">
                    <AuthLayout>
                      <OpportunitiesApply key="update" />
                    </AuthLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/syncs">
                    <AuthLayout>
                      <Syncs />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/syncs/apply">
                    <AuthLayout>
                      <SyncsApply key="create" />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/syncs/apply/:id">
                    <AuthLayout>
                      <SyncsApply key="update" />
                    </AuthLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/broadcasts">
                    <AuthLayout>
                      <Broadcasts />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/broadcasts/add">
                    <AuthLayout>
                      <BroadcastsCreateUpdate key="create" />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/broadcasts/add/:id">
                    <AuthLayout>
                      <BroadcastsCreateUpdate key="update" />
                    </AuthLayout>
                  </PrivateRoute>
                  <PrivateRoute exact path="/statements">
                    <AuthLayout>
                      <Statements />
                    </AuthLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/settings">
                    <AuthLayout>
                      <Settings />
                    </AuthLayout>
                  </PrivateRoute>

                  <Route exact path="/sign-up">
                    <BasicLayout>
                      <SignUp />
                    </BasicLayout>
                  </Route>

                  <Route exact path="/welcome">
                    <BasicLayout>
                      <Login />
                    </BasicLayout>
                  </Route>

                  <Route exact path="/sign-up-continue">
                    <BasicLayout>
                      <SignUpMessage />
                    </BasicLayout>
                  </Route>

                  <Route exact path="/confirm">
                    <BasicLayout>
                      <SignUpConfirm />
                    </BasicLayout>
                  </Route>

                  <Route exact path="/login">
                    <BasicLayout>
                      <Login />
                    </BasicLayout>
                  </Route>

                  <Route exact path="/terms-and-conditions">
                    <BasicLayout>
                      <Terms />
                    </BasicLayout>
                  </Route>

                  <Route exact path="/reset-password">
                    <BasicLayout>
                      <ResetPassword />
                    </BasicLayout>
                  </Route>

                  <Route exact path="/reset-password-message">
                    <BasicLayout>
                      <ResetPasswordMessage />
                    </BasicLayout>
                  </Route>

                  <Route exact path="/reset-password-success">
                    <BasicLayout>
                      <ResetPasswordSuccess />
                    </BasicLayout>
                  </Route>

                  <Route exact path="/reset">
                    <BasicLayout>
                      <ResetPasswordConfirm />
                    </BasicLayout>
                  </Route>

                  <PrivateRoute exact path="/onboarding-1">
                    <BasicLayout>
                      <Onboarding1 />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/onboarding-2">
                    <BasicLayout>
                      <Onboarding2 />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/onboarding-3">
                    <BasicLayout>
                      <Onboarding3 />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/add-account-pre">
                    <BasicLayout>
                      <AddAccountPre />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/add-account-1">
                    <BasicLayout>
                      <AddAccount1 />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/add-account-2">
                    <BasicLayout>
                      <AddAccount2 />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/add-account-3">
                    <BasicLayout>
                      <AddAccount3 />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/add-account-4">
                    <BasicLayout>
                      <AddAccount4 />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/add-account-5">
                    <BasicLayout>
                      <AddAccount5 />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/add-account-6">
                    <BasicLayout>
                      <AddAccount6 />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/add-twitter">
                    <BasicLayout>
                      <AddTwitter />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/add-instagram">
                    <BasicLayout>
                      <AddInstagram />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/add-facebook">
                    <BasicLayout>
                      <AddFacebook />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/add-songkick">
                    <BasicLayout>
                      <AddSongkick />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/add-bandsintown">
                    <BasicLayout>
                      <AddBandsintown />
                    </BasicLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/add-account-final">
                    <BasicLayout>
                      <AddAccountFinal />
                    </BasicLayout>
                  </PrivateRoute>
                </Switch>
              </Router>
            </Suspense>
          </QueryClientProvider>
        </ErrorBoundary>
      )}
    </div>
  );
}

export default withLDProvider({
  clientSideID: process.env.REACT_APP_LAUNCH_DARKLY_ID,
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
  // deferInitialization: true,
  options: {
    allAttributesPrivate: true,
  },
})(App);
